import React, { useCallback, useEffect, useState, useMemo } from "react";
import {
  Alert,
  Button,
  notification,
  Spin,
  Space,
  Empty,
  Row,
  Col,
} from "antd";
import axios from "axios";
import {
  ReloadOutlined,
  PlusOutlined,
  LoadingOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { Link, useHistory } from "react-router-dom";

import { useAuth } from "../../hooks/useAuth";
import useSegment from "../../hooks/useSegment";
import firebase from "../../services/firebase";
import { handleError } from "../../helpers";
import { SERVICE_LEVEL } from "../../constants";
import ClusterCard from "./ClusterCard";

const DASHBOARD_MAX_WIDTH = 1200;

const Spinner = (
  <LoadingOutlined style={{ fontSize: 48, margin: -24 }} spin />
);

function debounce(fn, ms) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      fn(...args);
    }, ms);
  };
}

const Dashboard = ({ clusters: passedClusters = null }) => {
  const auth = useAuth();
  const history = useHistory();
  const segment = useSegment(auth);
  const db = firebase.firestore();

  const [clustersLoading, setClustersLoading] = useState(false);
  const [clusters, setClusters] = useState(passedClusters);
  const [actions, setActions] = useState([]);

  const getClusters = useCallback(() => {
    const fetch = async () => {
      try {
        setClustersLoading(true);
        const token = await auth.getIdToken();
        const resp = await axios.get(
          `${process.env.REACT_APP_FB_FUNCTIONS_DOMAIN}/clusters`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const clusters = resp?.data?.clusters.filter(
          (cluster) => Object.keys(cluster).length > 1
        );

        setClusters(clusters);
      } catch (error) {
        handleError(error);
        setClusters([]);
      } finally {
        setClustersLoading(false);
      }
    };
    fetch();
  }, [auth]);

  const getActions = useCallback(() => {
    const fetch = async () => {
      try {
        const token = await auth.getIdToken();
        const resp = await axios.get(
          `${process.env.REACT_APP_FB_FUNCTIONS_DOMAIN}/actions`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const actions = resp?.data?.actions.filter(
          (action) => Object.keys(action).length > 1
        );

        setActions(actions);
      } catch (error) {
        handleError(error);
        setActions([]);
      }
    };
    fetch();
  }, [auth]);

  const refresh = useCallback(() => {
    getClusters();
    getActions();
  }, [getActions, getClusters]);

  const debouncedRefresh = useMemo(() => {
    return debounce(refresh, 400);
  }, [refresh]);

  useEffect(() => {
    const unsubscribe = db
      .collection("dashboards")
      .doc(auth.user.db.id)
      .onSnapshot((snapshot) => {
        getClusters();
        getActions();
      });
    return () => unsubscribe();
  }, [db, auth, getClusters, getActions]);

  const handleResend = async () => {
    try {
      await auth.sendEmailVerification();
      notification.info({
        message: "E-Mail Verification",
        description:
          "A new verification e-mail has been sent. Please check and click the verification link to confirm your account.",
        duration: 0,
      });
    } catch (error) {
      handleError(error);
    }
  };

  const handleRefreshClick = () => {
    debouncedRefresh();
  };

  const handleLoginClick = (cluster, username) => () => {
    segment.track("workbench_login");
    window.open(
      `${cluster.workbench_url}/workbench/login?username=${username}`
    );
  };

  const handleManageClick = (cluster) => () => {
    history.push(`/cluster/${cluster.id}`);
  };

  const handleTryDedicatedClick = () => {
    history.push(`/dedicated`);
  };

  const sortedClusters = useMemo(() => {
    if (!clusters) return [];
    return [...clusters].sort((a, b) => {
      if (a.service_level < b.service_level) return -1;
      if (a.service_level > b.service_level) return 1;
      if (a.cluster_label < b.cluster_label) return -1;
      if (a.cluster_label > b.cluster_label) return 1;
      return 0;
    });
  }, [clusters]);

  const showDedicatedPromo = useMemo(() => {
    return !sortedClusters.some(
      (cluster) => cluster.service_level === SERVICE_LEVEL.DEDICATED
    );
  }, [sortedClusters]);

  const getClusterActions = (cluster) =>
    actions.filter(
      (action) =>
        (action.cluster_id === cluster.id &&
          action.user_id === auth.user.db.id) ||
        (action.cluster_id === cluster.id && action.user_id === null)
    );

  if (clusters === null) {
    return (
      <div style={{ padding: "200px", textAlign: "center" }}>
        <Spin indicator={Spinner} size="large" />
      </div>
    );
  }

  if (!auth.user.emailVerified) {
    return (
      <div style={{ maxWidth: DASHBOARD_MAX_WIDTH }}>
        <h2 style={{ fontWeight: 300, marginBottom: 20 }}>Verification</h2>
        <Alert
          message="Warning"
          description={
            <>
              Your account{" "}
              <strong>{auth.user.db.email_addr.toLowerCase()}</strong> requires
              e-mail verification. You will receive an e-mail within a few
              minutes with a link to verify your account. If not, you can{" "}
              <Button
                type="link"
                onClick={handleResend}
                size="small"
                style={{ padding: "0px" }}
              >
                click here to resend
              </Button>
              .
            </>
          }
          type="warning"
        />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: DASHBOARD_MAX_WIDTH }}>
      <h1>Databases</h1>
      <div style={{ height: "5px" }}></div>
      <div
        style={{
          clear: "both",
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 28,
        }}
      >
        <Space>
          <Link to="/plans">
            <Button icon={<PlusOutlined />} type="primary" size="middle">
              New Database
            </Button>
          </Link>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefreshClick}
            loading={clustersLoading}
            size="middle"
          >
            Refresh
          </Button>
        </Space>
      </div>

      {sortedClusters.length === 0 ? (
        <Empty description="No Databases Found" style={{ marginTop: 60 }} />
      ) : (
        <Row gutter={[24, 24]}>
          {sortedClusters.map((cluster) => {
            const assignment = cluster.assignments.find(
              (a) => a.cluster_id === cluster.id
            );
            const clusterActions = getClusterActions(cluster);
            return (
              <Col key={cluster.id} xs={24} sm={24} md={12} lg={8} xl={8}>
                <ClusterCard
                  cluster={cluster}
                  assignment={assignment}
                  actions={clusterActions}
                  clustersLoading={clustersLoading}
                  onLogin={handleLoginClick(
                    cluster,
                    assignment?.admin_username
                  )}
                  onManage={handleManageClick(cluster)}
                />
              </Col>
            );
          })}
          {showDedicatedPromo && (
            <Col xs={24} sm={24} md={12} lg={8} xl={8}>
              <div className="dashboard-promo-card">
                <div aria-hidden className="dashboard-promo-card__accent" />
                <div aria-hidden className="dashboard-promo-card__glow" />

                <div className="dashboard-promo-card__pill">New</div>

                <h3 className="dashboard-promo-card__title">Kinetica Cloud</h3>

                <div className="dashboard-promo-card__meta">
                  <span>Dedicated</span>
                  <span className="dashboard-promo-card__meta-sep">·</span>
                  <span>Starting at $1.80/hr</span>
                </div>

                <ul className="dashboard-promo-card__features">
                  <li>No data limits</li>
                  <li>Dedicated compute</li>
                  <li>Full database functionality</li>
                </ul>

                <Button
                  type="primary"
                  size="large"
                  block
                  onClick={handleTryDedicatedClick}
                  className="kinetica-gradient-btn dashboard-promo-card__cta"
                >
                  Get Started <ArrowRightOutlined />
                </Button>
              </div>
            </Col>
          )}
        </Row>
      )}
    </div>
  );
};

export default Dashboard;
